// src/components/D3Cloud.tsx
import React, { useRef, useEffect, useCallback, memo } from "react";
import * as d3 from "d3"; // Use wildcard import for consistency
import { QuestionCategory } from "../data/questionData"; // Assuming this path is correct

function wrapText(context: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number) {
  const words = text.split(' ');
  let line = '';
  let lineCount = 0;
  const lines = [];

  // First, figure out how many lines we will have
  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    const metrics = context.measureText(testLine);
    const testWidth = metrics.width;
    if (testWidth > maxWidth && n > 0) {
      lines.push(line);
      line = words[n] + ' ';
    } else {
      line = testLine;
    }
  }
  lines.push(line);

  // Center the text block vertically
  const startY = y - (lineHeight * (lines.length - 1)) / 2;

  // Now draw the lines
  for (let k = 0; k < lines.length; k++) {
    context.fillText(lines[k], x, startY + (k * lineHeight));
  }
}

// Animation options - toggle effects on/off
const ANIMATION_OPTIONS = {
    enableFloating: false, // Start with all disabled
    enableBreathing: false,
    enableOpacityPulse: true,
    enableShimmer: true,
    enableRotation: false,
    enableTrails: false,
};

const PARTICLE_BASE_WIDTH = 11;
const PARTICLE_BASE_HEIGHT = 20.35;
const PARTICLE_SIZE_MULTIPLIER = 0.9; // <<< YOU WILL CHANGE THIS VALUE TO EXPERIMENT - was 0.75

const RESIZE_DEBOUNCE_MS = 250; // Debounce time for resize handler

interface D3CloudProps {
    questionData: QuestionCategory[];
    activeSection: number;
    scrollY: number;
    onCloudExitComplete?: () => void; // For handoff to the Features section
    neuronalSectionStartVh: number;
    neuronalSectionHeightVh: number;
    scrollDirection: 'down' | 'up';
    onNeuronalAnimationComplete?: () => void;
}

// Define particle interface (Updated for baseline consistency)
interface Particle {
    id: string;
    x: number;
    y: number;
    vx?: number;
    vy?: number;
    width: number;
    height: number;
    targetCenterX?: number; // Target X for floating state
    targetCenterY?: number; // Target Y for floating state
    drawLayer: 0 | 1;
    color: string;
    category: string;
    question: string | null;
    targetX?: number | null; // Used for question grid target
    targetY?: number | null; // Used for question grid target
    extendedWidth?: number;
    extendedHeight?: number;
    state:
    | "floating"
    | "movingToGrid" // Standardized state name
    | "arranging" // Keep as per baseline updateParticle logic
    | "in-section"
    | "returning"
    | "collapsing"
    | "frozen";
    progress: number;
    textOpacity?: number;
    trail?: Array<{ x: number; y: number; age: number }>;
    rotationSpeed?: number;
    startX?: number; // Keep for animations
    startY?: number; // Keep for animations
    rowIndex?: number; // For question grid stagger
    colIndex?: number; // For question grid stagger
    widthAtArrangeStart?: number; // Added/Confirmed Present
    heightAtArrangeStart?: number; // Added/Confirmed Present
    frozenX?: number;      // <<< ADD
    frozenY?: number;      // <<< ADD
    fx?: number | null;    // <<< ADD
    fy?: number | null;    // <<< ADD
    appearDelay?: number;  // <-- Add this if not already there
    hasAppeared?: boolean; // <-- Add this if not already there
    introStartX?: number;
    introStartY?: number;
    introTargetX?: number;
    introTargetY?: number;
    introProgress?: number;
    introDelay?: number;
    targetWidth?: number;     // <<< ADD/ENSURE THIS
    targetHeight?: number;    // <<< ADD/ENSURE THIS
    opacity?: number;         // <<< ADD/ENSURE THIS (if not already part of your Particle by implication)
    hasBeenLaunched?: boolean;
    exitDelay?: number;
}

const D3Cloud: React.FC<D3CloudProps> = ({
    questionData,
    activeSection,
    scrollY,
    onCloudExitComplete,
    // Destructure the new props here
    neuronalSectionStartVh,
    neuronalSectionHeightVh,
    scrollDirection,
    onNeuronalAnimationComplete,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const simulationRef = useRef<d3.Simulation<Particle, undefined> | null>(null);
    const particlesRef = useRef<Particle[]>([]);
    const activeSectionRef = useRef<number>(0);
    const activeCategoryRef = useRef<string | null>(null);
    const animationFrameRef = useRef<number>(0);
    const fontLoadedRef = useRef<boolean>(false);
    const scrollYRef = useRef<number>(0);
    const activeGridBoundsRef = useRef<{
  bounds: { minX: number; maxX: number; minY: number; };
  categoryId: string;
} | null>(null);
    const headerAnimStartRef = useRef<number | null>(null); // Stores timestamp when animation starts (or null)
    const headerTargetYRef = useRef<number>(0); // Stores the final calculated Y position for the header
    const headerTargetOpacityRef = useRef<number>(0.8); // Target opacity (matches text style)
    const headerOpacityRef = useRef(0);
    const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastHeaderChangeTimeRef = useRef<number>(0); // Initialize with 0, as the logic checks against 0

    const connectionsRef = useRef<Array<{fromId: string, toId: string, animProgress: number, id: string}>>([]);
  const connectionsCalculatedRef = useRef<boolean>(false);
  const lastBatchTimeRef = useRef<number>(0);
  const linesAnimatingCountRef = useRef<number>(0);
  const cloudExitTriggeredRef = useRef<boolean>(false); // Add this ref to track the exit trigger
  const handoffCompleteRef = useRef<boolean>(false);
  const animCompleteCalledRef = useRef<boolean>(false);
  const cloudExitOffsetRef = useRef<number>(0);
  const cloudExitStartedRef = useRef<boolean>(false);
const cloudExitProgressRef = useRef<number>(0);

    // Flocking function (keep for now)
    const getFlockingForce = (
        particles: Particle[],
        particle: Particle,
        range: number,
        strength: number
    ) => {
        // Only apply flocking to particles in the same category and same state
        const neighbors = particles.filter(
            (p) =>
                p.id !== particle.id &&
                p.category === particle.category &&
                p.state === particle.state
        );
        let avgX = 0;
        let avgY = 0;
        let count = 0;
        for (const neighbor of neighbors) {
            const dx = neighbor.x - particle.x;
            const dy = neighbor.y - particle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance > 0 && distance < range) {
                // Check distance > 0
                avgX += neighbor.x;
                avgY += neighbor.y;
                count++;
            }
        }
        if (count > 0) {
            avgX /= count;
            avgY /= count;
            return {
                x: (avgX - particle.x) * strength,
                y: (avgY - particle.y) * strength,
            };
        }
        return { x: 0, y: 0 };
    };

    useEffect(() => {
        const currentSection = activeSection; // Use local variable for clarity in logs
    
        // --- THIS IS THE NEW CODE TO ADD ---
        // This resets our "signal sent" flag whenever we leave section 11,
        // ensuring the handoff can happen again if the user scrolls back and forth.
        if (currentSection !== 11) {
            animCompleteCalledRef.current = false;
        }

        if (currentSection === 12 && window.innerWidth < 768) {
    // AUTO-TRIGGER CLOUD EXIT WHEN JUMPING TO SECTION 12 ON MOBILE
    cloudExitStartedRef.current = true;
}
    
        const isPostQuestionPhase = currentSection === 10;
    
        const newActiveCategory =
            currentSection > 0 && !isPostQuestionPhase
                ? questionData[currentSection - 1]?.id
                : null;
    
        activeSectionRef.current = currentSection;
        activeCategoryRef.current = newActiveCategory;
    
        updateParticleStates();
    
        // --- Header Fade Logic (Replaced with Timer Management) ---
        const isPostQ = currentSection === 10;
        const isCirc = currentSection === 11;
        const isPostCirc = currentSection === 12;
        const isGridSection =
            currentSection >= 1 && currentSection <= questionData.length;
    
        // Check if the header *should* start fading IN
        if (isGridSection) {
            // Header should be active/fading in for sections 1-9.
            // ALWAYS reset the timer when entering OR switching between grid sections.
            lastHeaderChangeTimeRef.current = performance.now();
        } else {
            // Header should be hidden for Section 0, 10, 11, 12 etc.
            if (
                lastHeaderChangeTimeRef.current &&
                lastHeaderChangeTimeRef.current > 0
            ) {
                // Check if it was previously visible/timing
            }
            lastHeaderChangeTimeRef.current = 0; // Use 0 to signal header is inactive/hidden for animate function
        }
    }, [activeSection, questionData]);

    useEffect(() => {
        // Remove the particle existence check - we want to initialize fresh
        
        // Wrap initialization in a function to call after font loading
        const initializeAndStart = () => {
            try {
                initializeParticles(); // Creates all particles at once
                
                // Setup simulation immediately (particles exist but are invisible)
                setupSimulation();
                
                startAnimation();
                
            } catch (error) {
                console.error("!!! D3Cloud: Error during setup:", error);
            }
        };
    
        // Font loading check (keeping your existing font loading logic)
        if (typeof document !== "undefined" && "fonts" in document) {
            document.fonts
                .load('500 20px "Inter"') // Ensure font weight/size matches usage
                .then(() => {
                    fontLoadedRef.current = true;
                    initializeAndStart();
                })
                .catch((err) => {
                    console.error("D3Cloud: Font loading failed or timed out.", err);
                    // Proceed anyway, text might look wrong initially
                    initializeAndStart();
                });
        } else {
            // Fallback if font loading API not supported
            initializeAndStart();
        }
    
        // Cleanup function
        return () => {
            if (simulationRef.current) {
                simulationRef.current.stop();
            }
            cancelAnimationFrame(animationFrameRef.current);
            // Remove resize listener if added in startAnimation
            window.removeEventListener("resize", updateCanvasSize);
        };
    }, []); // Empty dependency array ensures this runs only once on mount

    useEffect(() => {
        scrollYRef.current = scrollY;
        // Optional log to confirm ref is updating
    }, [scrollY]); // Dependency: This effect runs when the scrollY prop changes

    // Ensure updateCanvasSize is memoized using useCallback
    const updateCanvasSize = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (!canvas || !ctx) return;

        const dpr = window.devicePixelRatio || 1;
        const width = window.innerWidth;
        const height = window.innerHeight;

        // Only resize if dimensions actually changed to avoid unnecessary redraws/scaling
        if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
            canvas.width = width * dpr;
            canvas.height = height * dpr;
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            ctx.scale(dpr, dpr); // Rescale context after resizing buffer

            // Reheat simulation slightly on resize if it's active
            // Note: Baseline code had this commented out, uncommenting it here based on later plans. Adjust if needed.
            if (simulationRef.current && simulationRef.current.alpha() > 0.01) {
                // Check if simulation is running
                simulationRef.current.alpha(0.1).restart();
            }
        }
    }, []); // Empty dependency array is correct here as it only uses refs and browser globals

    const handleResize = useCallback(() => {
        // Update canvas size first
        updateCanvasSize();
    }, [updateCanvasSize]); // Dependency remains updateCanvasSize

    // Effect to set up debounced resize listener
    useEffect(() => {
        const debouncedHandler = () => {
            // Check if a timeout is already pending
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
            // Set a new timeout
            resizeTimeoutRef.current = setTimeout(() => {
                handleResize(); // Call the memoized handler
            }, RESIZE_DEBOUNCE_MS); // Use the constant for delay
        };
        window.addEventListener("resize", debouncedHandler);

        // Initial call to set size correctly when component mounts?
        // updateCanvasSize(); // Let's ensure initial size is set by mount effect or initial call

        // Cleanup listener on component unmount
        return () => {
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current); // Clear timeout on unmount
            }
            window.removeEventListener("resize", debouncedHandler);
        };
    }, [handleResize]); // Dependency array includes the memoized handleResize

    // Helper function to map colors to category IDs
    const getCategoryIdForColor = (color: string): string => {
        const category = questionData.find((cat) => cat.backgroundColor === color);
        return category ? category.id : "unknown";
    };

     // INITIALIZE....PARTICLES...INITIALIZE...PARTICLES...INITIALIZE...PARTICLES
    // INITIALIZE....PARTICLES...INITIALIZE...PARTICLES...INITIALIZE...PARTICLES
    // INITIALIZE....PARTICLES...INITIALIZE...PARTICLES...INITIALIZE...PARTICLES

    const initializeParticles = () => {
        particlesRef.current = [];
        const particles: Particle[] = [];
        const width = window.innerWidth;
        const height = window.innerHeight;
        const centerX = width / 2;
        const centerY = height / 2;
    
        const currentParticleBaseWidth = PARTICLE_BASE_WIDTH * PARTICLE_SIZE_MULTIPLIER;
        const currentParticleBaseHeight = PARTICLE_BASE_HEIGHT * PARTICLE_SIZE_MULTIPLIER;
    
        const centerSpawnRadius = Math.min(width, height) * 0.01; 
        const FOUNTAIN_DURATION = 8000;
        let particleIndex = 0;
        
        const colorCounts: Record<string, { count: number; max: number }> = {
            "#E4CCFF": { count: 0, max: 100 }, "#CBD6FF": { count: 0, max: 160 },
            "#BDDFFF": { count: 0, max: 220 }, "#B0F7FF": { count: 0, max: 95 },
            "#B0F4C6": { count: 0, max: 95 }, "#DAF4AF": { count: 0, max: 65 },
            "#EBF5B0": { count: 0, max: 40 }, "#FFE870": { count: 0, max: 15 },
            "#FFD5A5": { count: 0, max: 8 },
        };
    
        // Calculate total particles for timing
        const totalParticles = Object.values(colorCounts).reduce((sum, c) => sum + c.max, 0) + 
                             questionData.reduce((sum, cat) => sum + cat.questions.length, 0);
    
        // === NEW: Process each color wave, mixing in question particles of that color ===
        Object.entries(colorCounts).forEach(([color, config]) => {
            const waveParticles: Particle[] = [];
            
            // 1. Create background particles for this color
            for (let i = 0; i < config.max; i++) {
                const angle = Math.random() * Math.PI * 2;
                const radius = Math.sqrt(Math.random()) * centerSpawnRadius;
                const targetX = centerX + Math.cos(angle) * radius;
                const targetY = centerY + Math.sin(angle) * radius;
    
                const sizeVariation = 0.9 + Math.random() * 0.2;
                const finalWidth = currentParticleBaseWidth * sizeVariation;
                const finalHeight = currentParticleBaseHeight * sizeVariation;
    
                const particle: Particle = {
                    id: `bg-${color}-${i}`, x: targetX, y: targetY,
                    vx: 0, vy: 0, // KEEPING YOUR ORIGINAL: Start with no velocity
                    width: 0, height: 0, targetCenterX: targetX, targetCenterY: targetY,
                    drawLayer: Math.random() < 0.5 ? 0 : 1, color: color,
                    category: getCategoryIdForColor(color), question: null, state: "floating",
                    progress: 0, textOpacity: 0, fx: null, fy: null, opacity: 0,
                    targetWidth: finalWidth, targetHeight: finalHeight,
                    appearDelay: 0, hasAppeared: false, introProgress: 0,
                    hasBeenLaunched: false,
                };
                
                waveParticles.push(particle);
            }
    
            // 2. Find and create question particles that match this color
            questionData.forEach((category) => {
                if (category.backgroundColor === color) {
                    category.questions.forEach((question) => {
                        const angle = Math.random() * Math.PI * 2;
                        const radius = Math.sqrt(Math.random()) * centerSpawnRadius;
                        const targetX = centerX + Math.cos(angle) * radius;
                        const targetY = centerY + Math.sin(angle) * radius;
                        
                        const sizeVariation = 0.9 + Math.random() * 0.2;
                        const finalWidth = currentParticleBaseWidth * sizeVariation;
                        const finalHeight = currentParticleBaseHeight * sizeVariation;
    
                        const particle: Particle = {
                            id: question.id, x: targetX, y: targetY,
                            vx: 0, vy: 0, // KEEPING YOUR ORIGINAL: Start with no velocity
                            width: 0, height: 0, targetCenterX: targetX, targetCenterY: targetY,
                            drawLayer: Math.random() < 0.5 ? 0 : 1, color: category.backgroundColor,
                            category: category.id, question: question.text, state: "floating",
                            progress: 0, textOpacity: 0, fx: null, fy: null, opacity: 0,
                            targetWidth: finalWidth, targetHeight: finalHeight,
                            appearDelay: 0, hasAppeared: false, introProgress: 0,
                            hasBeenLaunched: false,
                        };
                        
                        waveParticles.push(particle);
                    });
                }
            });
    
            // 3. Shuffle particles within this color wave
            for (let i = waveParticles.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [waveParticles[i], waveParticles[j]] = [waveParticles[j], waveParticles[i]];
            }
    
            // 4. Assign timing and add to main array
            waveParticles.forEach((particle) => {
                const appearanceTime = (particleIndex / totalParticles) * FOUNTAIN_DURATION;
                const jitter = (Math.random() - 0.5) * 400;
                particle.appearDelay = appearanceTime + jitter;
                
                particles.push(particle);
                particleIndex++;
            });
        });
    
        particlesRef.current = particles;
    };

    // Replace the entire calculateQuestionPositions function with this:
    const calculateQuestionPositions = (category: QuestionCategory) => {
        const positions: {
            x: number; y: number; width: number; height: number;
            rowIndex: number; colIndex: number;
        }[] = [];

        let minX = Infinity, maxX = -Infinity, minY = Infinity;

        const width = window.innerWidth;
        const height = window.innerHeight;
        const ctx = canvasRef.current?.getContext("2d");

        // <<< START: RESPONSIVE LAYOUT LOGIC >>>
        const isMobile = width < 768;

        if (isMobile) {
            // --- MOBILE LAYOUT: Single, Centered Column ---
            const SCALE_FACTOR = 0.9; // Slightly smaller pills on mobile
            const PILL_HEIGHT = 36 * SCALE_FACTOR;
            const PILL_PADDING = 14 * SCALE_FACTOR;
            const MIN_PILL_WIDTH = 90 * SCALE_FACTOR;
            const VERTICAL_GAP = 12 * SCALE_FACTOR;

            const questionPills = category.questions.map(question => {
                let measuredTextWidth = question.text.length * 8; // Fallback
                if (ctx && fontLoadedRef.current) {
                    ctx.font = `300 ${18 * SCALE_FACTOR}px "Inter", sans-serif`;
                    measuredTextWidth = ctx.measureText(question.text).width;
                }
                const pillWidth = Math.max(measuredTextWidth + PILL_PADDING * 2, MIN_PILL_WIDTH);
                return { text: question.text, width: pillWidth };
            });

            const totalHeight = questionPills.length * PILL_HEIGHT + (questionPills.length - 1) * VERTICAL_GAP;
            const maxPillWidth = Math.max(...questionPills.map(p => p.width));
            
            const layoutStartX = (width - maxPillWidth) / 2; // Center the column
            let currentY = (height - totalHeight) / 2;

            minX = layoutStartX;
            minY = currentY;

            questionPills.forEach((pill, index) => {
                positions.push({
                    x: layoutStartX,
                    y: currentY,
                    width: pill.width,
                    height: PILL_HEIGHT,
                    rowIndex: index,
                    colIndex: 0
                });
                currentY += PILL_HEIGHT + VERTICAL_GAP;
            });
            
            maxX = minX + maxPillWidth;

        } else {
            // --- DESKTOP LAYOUT: Your Original Multi-Column Grid Logic ---
            const SCALE_FACTOR = 0.95;
            const HORIZONTAL_GAP = 15 * SCALE_FACTOR;
            const VERTICAL_GAP = 15 * SCALE_FACTOR;
            const PILL_HEIGHT = 38 * SCALE_FACTOR;
            const PILL_PADDING = 16 * SCALE_FACTOR;
            const MIN_PILL_WIDTH = 100 * SCALE_FACTOR;

            const crescentApproxRightEdge = width * (0.2 + 0.1 * 1.2);
            const rightMargin = width * 0.05;
            const availableWidth = width - crescentApproxRightEdge - rightMargin;
            const layoutWidth = Math.min(availableWidth * 0.9, width * 0.5);
            const layoutStartX = crescentApproxRightEdge + (availableWidth - layoutWidth) / 2;
            const layoutEndX = layoutStartX + layoutWidth;

            // Row Counting Pass (Original Logic)
            let currentX = layoutStartX;
            let rowCount = 1;
            category.questions.forEach((question) => {
                let measuredTextWidth = question.text.length * 8.5 * SCALE_FACTOR;
                if (ctx && fontLoadedRef.current) {
                    ctx.font = `300 ${20 * SCALE_FACTOR}px "Inter", sans-serif`;
                    measuredTextWidth = ctx.measureText(question.text).width;
                }
                const pillWidth = Math.max(measuredTextWidth + PILL_PADDING * 2, MIN_PILL_WIDTH);
                if (currentX !== layoutStartX && currentX + pillWidth > layoutEndX) {
                    currentX = layoutStartX;
                    rowCount++;
                }
                currentX += pillWidth + HORIZONTAL_GAP;
            });

            const totalHeight = rowCount * PILL_HEIGHT + (rowCount - 1) * VERTICAL_GAP;
            const initialY = height * 0.5 - totalHeight / 2;

            // Actual Layout Pass (Original Logic)
            currentX = layoutStartX;
            let currentY = initialY;
            let rowIndex = 0;
            let colIndex = 0;

            category.questions.forEach((question) => {
                let measuredTextWidth = question.text.length * 8.5 * SCALE_FACTOR;
                if (ctx && fontLoadedRef.current) {
                    ctx.font = `300 ${20 * SCALE_FACTOR}px "Inter", sans-serif`;
                    measuredTextWidth = ctx.measureText(question.text).width;
                }
                const pillWidth = Math.max(measuredTextWidth + PILL_PADDING * 2, MIN_PILL_WIDTH);

                if (currentX !== layoutStartX && currentX + pillWidth > layoutEndX) {
                    currentX = layoutStartX;
                    currentY += PILL_HEIGHT + VERTICAL_GAP;
                    rowIndex++;
                    colIndex = 0;
                }

                positions.push({
                    x: currentX, y: currentY, width: pillWidth, height: PILL_HEIGHT,
                    rowIndex: rowIndex, colIndex: colIndex,
                });

                minX = Math.min(minX, currentX);
                maxX = Math.max(maxX, currentX + pillWidth);
                minY = Math.min(minY, currentY);

                currentX += pillWidth + HORIZONTAL_GAP;
                colIndex++;
            });
        }
        // <<< END: RESPONSIVE LAYOUT LOGIC >>>

        if (!isFinite(minX)) {
             minX = width / 2;
             maxX = width / 2;
             minY = height / 2;
        }

        return {
            positions,
            bounds: { minX, maxX, minY },
        };
    }; // --- End of calculateQuestionPositions ---

    // SETUP....SIMULATION...SETUP...SIMULATION...SETUP...SIMULATION
    // SETUP....SIMULATION...SETUP...SIMULATION...SETUP...SIMULATION
    // SETUP....SIMULATION...SETUP...SIMULATION...SETUP...SIMULATION

    const setupSimulation = () => {
        if (!particlesRef.current || particlesRef.current.length === 0) {
            setTimeout(() => setupSimulation(), 500);
            return;
        }
    
        const width = window.innerWidth;
        const height = window.innerHeight;
        const centerX = width / 2;
        const centerY = height / 2;
        
        // A large boundary for the particles to fill.
        const boundaryRadius = Math.min(width, height) * 0.45;
        
        // --- ENERGETIC & SUSTAINED PHYSICS VALUES ---
        // The engine of the sustained float, stronger now.
        const BROWNIAN_STRENGTH = 0.01; // 0.008 was the initially liked one
        // A slightly stronger boundary to encourage mixing.
        const BOUNDARY_STRENGTH = 0.05; 
        const COLLISION_STRENGTH = 0.02;
        const FINAL_FRICTION = 0.009; 
        const SIMULATION_ENERGY = 0.15;
        
        const simulationStartTime = performance.now();
        const INTRO_DURATION = 10000; // A long, smooth transition.
        
        // --- CLAUDE'S "SPAWN IMMUNITY" FIX ---
        // This is the key to solving the donut hole. Invisible particles have almost no collision radius.
        const collisionForce = d3.forceCollide<Particle>()
          .radius(p => (!p.hasAppeared ? 0.1 : p.width / 2 + 1))
          .strength(0); // Strength will be ramped up by the timer.
    
        const simulation = d3.forceSimulation<Particle>(particlesRef.current)
            .force("collide", collisionForce)
            .force("brownianMotion", () => {}) // Placeholder, controlled by timer
            .force("boundary", () => {})   // Placeholder, controlled by timer
            .velocityDecay(0.008)           // Start with VERY LOW friction to let the burst travel.
            .alpha(0.3);                      // Start with high energy for the initial impulse.
    
        const updateForces = () => {
            const elapsed = performance.now() - simulationStartTime;
            const progress = Math.min(1, elapsed / INTRO_DURATION);
            const easedProgress = d3.easeCubicOut(progress);
            
            // --- MY "ACTIVE ENVIRONMENT" FIX ---
            // Ramp up forces and friction over time for a smooth handoff.
            const minStrength = 0.15; // Start ongoing forces at 40%
            const currentRamp = minStrength + (1 - minStrength) * easedProgress;
    
            const currentBrownian = BROWNIAN_STRENGTH * currentRamp;
            const currentBoundaryStrength = BOUNDARY_STRENGTH * currentRamp;
            const currentCollisionStrength = COLLISION_STRENGTH * currentRamp;
            const currentFriction = 0.001 + (FINAL_FRICTION - 0.001) * progress;
    
            // Apply the updated forces
            collisionForce.strength(currentCollisionStrength);
            simulation.velocityDecay(currentFriction);
            simulation.alphaTarget(SIMULATION_ENERGY * progress); // Gently ramp up final energy
    
            simulation.force("brownianMotion", () => {
                particlesRef.current.forEach(p => {
                    if (p.state === "floating" && !p.fx && !p.fy && p.hasAppeared) { // Add hasAppeared check!
                        p.vx = (p.vx || 0) + (Math.random() - 0.5) * currentBrownian;
                        p.vy = (p.vy || 0) + (Math.random() - 0.5) * currentBrownian;
                    }
                });
            });
    
            simulation.force("boundary", () => {
                particlesRef.current.forEach(p => {
                    if (p.state === "floating" && !p.fx && !p.fy && p.hasAppeared) {
                        const dx = p.x - centerX;
                        const dy = p.y - centerY;
                        const distance = Math.sqrt(dx * dx + dy * dy);
                        if (distance > boundaryRadius) {
                            const strength = currentBoundaryStrength * 0.1;
                            // Fix: Provide default values for vx and vy
                            p.vx = (p.vx || 0) - (dx / distance) * (distance - boundaryRadius) * strength;
                            p.vy = (p.vy || 0) - (dy / distance) * (distance - boundaryRadius) * strength;
                        }
                    }
                });
            });
            
            if (progress < 1) {
                setTimeout(updateForces, 50);
            } else {
            }
        };
        
        setTimeout(updateForces, 50);
        
        simulationRef.current = simulation;
    };

    // SETUP....SIMULATION...SETUP...SIMULATION...SETUP...SIMULATION
    // SETUP....SIMULATION...SETUP...SIMULATION...SETUP...SIMULATION
    // SETUP....SIMULATION...SETUP...SIMULATION...SETUP...SIMULATION

    // In D3Cloud.tsx

    const calculateNeuronalConnections = () => {
        const frozenParticles = particlesRef.current.filter(p => p.state === "frozen");
        
        if (frozenParticles.length < 2) {
            return [];
        }
        
        const connections: Array<{fromId: string, toId: string, animProgress: number, id: string}> = [];
        
        // For each frozen particle, find its 2-3 nearest neighbors
        frozenParticles.forEach(particle => {
            // Calculate distances to all other frozen particles
            const distances = frozenParticles
            .filter(other => other.id !== particle.id && other.color === particle.color)
                .map(other => ({
                    particle: other,
                    distance: Math.sqrt(
                        Math.pow(particle.x - other.x, 2) + 
                        Math.pow(particle.y - other.y, 2)
                    )
                }))
                .sort((a, b) => a.distance - b.distance);
            
            // Connect to 2-3 closest neighbors (but not if they're too far)
            const maxDistance = Math.min(window.innerWidth, window.innerHeight) * 0.5; // Max 15% of screen
            const maxConnections = 2 + Math.floor(Math.random() * 2); // 2-3 connections
            
            for (let i = 0; i < Math.min(maxConnections, distances.length); i++) {
                const neighbor = distances[i];
                if (neighbor.distance > maxDistance) break; // Too far
                
                // Avoid duplicate connections (A->B and B->A)
                const connectionId1 = `${particle.id}-${neighbor.particle.id}`;
                const connectionId2 = `${neighbor.particle.id}-${particle.id}`;
                
                const alreadyExists = connections.some(conn => 
                    conn.id === connectionId1 || conn.id === connectionId2
                );
                
                if (!alreadyExists) {
                    connections.push({
                        fromId: particle.id,
                        toId: neighbor.particle.id,
                        animProgress: 0, // Will animate from 0 to 1
                        id: connectionId1
                    });
                }
            }
        });
        return connections;
    };
    
    const updateConnections = (deltaTime: number) => {
        if (!connectionsRef.current || connectionsRef.current.length === 0) return;
        
        const currentTime = performance.now();
        const ANIMATION_DURATION = 1; // Duration for each line to grow
        const BATCH_SIZE = 25; // Number of connections to start animating at once
        const BATCH_DELAY = 1; // Delay between batches
        
        // Start new batches if needed
        if (lastBatchTimeRef.current === 0) {
            lastBatchTimeRef.current = currentTime;
            linesAnimatingCountRef.current = 0;
        }
        
        // Check if we should start a new batch
const timeSinceLastBatch = currentTime - lastBatchTimeRef.current;
const inactiveConnections = connectionsRef.current.filter(conn => conn.animProgress === 0);
const activeConnections = connectionsRef.current.filter(conn => conn.animProgress > 0 && conn.animProgress < 1);

if (timeSinceLastBatch >= BATCH_DELAY && inactiveConnections.length > 0 && activeConnections.length < BATCH_SIZE) {
    // Group inactive connections by color
    const connectionsByColor: { [color: string]: typeof inactiveConnections } = {};
    inactiveConnections.forEach(conn => {
        const fromParticle = particlesRef.current.find(p => p.id === conn.fromId);
        if (fromParticle) {
            if (!connectionsByColor[fromParticle.color]) {
                connectionsByColor[fromParticle.color] = [];
            }
            connectionsByColor[fromParticle.color].push(conn);
        }
    });
    
    // Start one connection from each color group
    const connectionsToStart: typeof inactiveConnections = [];
    Object.values(connectionsByColor).forEach(colorConnections => {
        if (colorConnections.length > 0) {
            connectionsToStart.push(colorConnections[0]);
        }
    });
    
    // Start animating the selected connections
    connectionsToStart.forEach(conn => {
        conn.animProgress = 0.001; // Start animation (just above 0)
    });
    
    lastBatchTimeRef.current = currentTime;
    linesAnimatingCountRef.current += connectionsToStart.length;
}
        
        // Update animation progress for active connections
        connectionsRef.current.forEach(connection => {
            if (connection.animProgress > 0 && connection.animProgress < 1) {
                connection.animProgress = Math.min(1, connection.animProgress + deltaTime / (ANIMATION_DURATION / 1000));
            }
        });
    };
    
    const drawConnections = (ctx: CanvasRenderingContext2D) => {
        if (!connectionsRef.current || connectionsRef.current.length === 0) return;
        
        ctx.save();
// We'll set strokeStyle for each connection individually
ctx.lineWidth = 1.7; // Very thin lines
        ctx.lineCap = "round";
        
        connectionsRef.current.forEach(connection => {
            if (connection.animProgress <= 0) return; // Not started yet
            
            const fromParticle = particlesRef.current.find(p => p.id === connection.fromId);
            const toParticle = particlesRef.current.find(p => p.id === connection.toId);
            
            if (!fromParticle || !toParticle) return;

            // Set line color to match the particle color (with transparency)
const particleColor = fromParticle.color;
// Convert hex to rgba with transparency
const r = parseInt(particleColor.slice(1, 3), 16);
const g = parseInt(particleColor.slice(3, 5), 16);
const b = parseInt(particleColor.slice(5, 7), 16);
ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.4)`;
            
            // Calculate the end point based on animation progress
            const progress = d3.easeCubicOut(connection.animProgress); // Smooth easing
            const endX = fromParticle.x + (toParticle.x - fromParticle.x) * progress;
            const endY = fromParticle.y + (toParticle.y - fromParticle.y) * progress;
            
            // Draw the line from start to current end point
            ctx.beginPath();
            ctx.moveTo(fromParticle.x + fromParticle.width/2, fromParticle.y + fromParticle.height/2);
            ctx.lineTo(endX + (toParticle.width/2 * progress), endY + (toParticle.height/2 * progress));
            ctx.stroke();
        });
        
        ctx.restore();
    };

    const updateParticleStates = () => {
        const currentSectionIndex = activeSectionRef.current;

        const IS_NEURONAL_SECTION = currentSectionIndex === 11;
      
        const isGridSection = currentSectionIndex >= 1 && currentSectionIndex <= questionData.length;
        const targetCategoryData = isGridSection ? questionData[currentSectionIndex - 1] : null;
        const targetCategoryId = targetCategoryData?.id ?? null;
        const targetDataMap: Record<string, ReturnType<typeof calculateQuestionPositions>> = {};
        if (isGridSection && targetCategoryData && targetCategoryId) {
            activeGridBoundsRef.current = null;
            try {
                const calculatedData = calculateQuestionPositions(targetCategoryData);
                targetDataMap[targetCategoryId] = calculatedData;
                activeGridBoundsRef.current = { bounds: calculatedData.bounds, categoryId: targetCategoryId };
            } catch (error) { console.error(`Err calc grid pos ${targetCategoryId}:`, error); activeGridBoundsRef.current = null; }
        } else {
            activeGridBoundsRef.current = null;
        }
      
        let changedStateCount = 0;
      
        // REPLACE your entire `particlesRef.current.forEach` loop with this corrected version:
particlesRef.current.forEach((particle) => {
    const previousState = particle.state;

    if (IS_NEURONAL_SECTION) {
        // When in the neuronal section, freeze ALL particles.
        if (particle.state !== "frozen") {
            particle.state = "frozen";
            particle.frozenX = particle.x;
            particle.frozenY = particle.y;
            particle.fx = particle.frozenX; // PIN using D3's fixed position
            particle.fy = particle.frozenY; // PIN using D3's fixed position
            particle.vx = 0; 
            particle.vy = 0;
        }
    } else { 
        // This is the logic for all other sections.

        // If a particle was part of the neuronal cloud, unfreeze it, but NOT during the Section 12 overlap.
        if (particle.state === "frozen" && currentSectionIndex !== 12) {
            particle.state = "floating";
            particle.frozenX = undefined;
            particle.frozenY = undefined;
        }
        
        // Always ensure particles are unpinned if they are not in the 'frozen' state.
        if (particle.state !== 'frozen') {
             particle.fx = null;
             particle.fy = null;
        }

        // --- This is your existing logic for the question grid sections, now correctly placed ---
        const isPostQuestionPhase = currentSectionIndex === 10;
        const shouldBeActiveInGridOriginal = isGridSection && particle.question && particle.category === targetCategoryId;
        const isCurrentlyVisibleInGridOriginal = particle.state === "in-section" || particle.state === "movingToGrid" || particle.state === "arranging";
        const canTransitionOriginal = particle.state === "floating" || particle.state === "returning";

        if (isPostQuestionPhase && isCurrentlyVisibleInGridOriginal) {
            if (particle.state !== "returning") {
                particle.state = "returning"; particle.progress = 0;
                particle.targetX = null; particle.targetY = null; particle.extendedWidth = undefined; particle.extendedHeight = undefined;
                particle.widthAtArrangeStart = undefined; particle.heightAtArrangeStart = undefined;
                particle.startX = particle.x; particle.startY = particle.y;
            }
        } else if (shouldBeActiveInGridOriginal && canTransitionOriginal) {
            if (particle.state !== "movingToGrid") {
                const targetData = targetCategoryId ? targetDataMap[targetCategoryId] : null;
                if (!targetData?.positions) { particle.state = "floating"; particle.progress = 0; return; }
                const qIndex = questionData.find((c) => c.id === particle.category)?.questions.findIndex((q) => q.id === particle.id) ?? -1;
                if (qIndex !== -1 && qIndex < targetData.positions.length) {
                    const targetPos = targetData.positions[qIndex];
                    if (targetPos?.x != null && targetPos?.y != null && targetPos?.width != null && targetPos?.height != null) {
                        particle.state = "movingToGrid"; particle.progress = 0;
                        particle.startX = particle.x; particle.startY = particle.y;
                        particle.targetX = targetPos.x; particle.targetY = targetPos.y;
                        particle.extendedWidth = targetPos.width; particle.extendedHeight = targetPos.height;
                        particle.rowIndex = targetPos.rowIndex; particle.colIndex = targetPos.colIndex;
                        particle.widthAtArrangeStart = undefined; particle.heightAtArrangeStart = undefined;
                    } else { console.warn(`P:${particle.id} Invalid targetPos from calcQPos`); }
                } else { console.warn(`P:${particle.id} Cannot find Q index`); }
            }
        } else if (!shouldBeActiveInGridOriginal && isCurrentlyVisibleInGridOriginal && !isPostQuestionPhase) {
            if (particle.state !== "returning") {
                particle.state = "returning"; particle.progress = 0;
                particle.targetX = null; particle.targetY = null; particle.extendedWidth = undefined; particle.extendedHeight = undefined;
                particle.widthAtArrangeStart = undefined; particle.heightAtArrangeStart = undefined;
                particle.startX = particle.x; particle.startY = particle.y;
            }
        }
        // --- End of question grid logic ---
    }

    if (particle.state !== previousState) {
        changedStateCount++;
    }
});
      
        if (changedStateCount > 0)
      
        // Neuronal connection calculation
if (IS_NEURONAL_SECTION && !connectionsCalculatedRef.current) {
    // Calculate connections when first entering section 11
    connectionsRef.current = calculateNeuronalConnections();
    connectionsCalculatedRef.current = true;
    linesAnimatingCountRef.current = 0;
    lastBatchTimeRef.current = 0;
} else if (!IS_NEURONAL_SECTION && connectionsCalculatedRef.current) {
    // Clear connections when leaving section 11
    connectionsRef.current = [];
    connectionsCalculatedRef.current = false;
    linesAnimatingCountRef.current = 0;
    lastBatchTimeRef.current = 0;
}
      };
      



    // In D3Cloud.tsx
    // Replace the existing drawTitleCard function with this:

    const drawTitleCard = (
    ctx: CanvasRenderingContext2D,
    currentScrollY: number,
    currentActiveSection: number
) => {
    if (currentActiveSection !== 0) {
        return null;
    }

    const width = window.innerWidth;
    const height = window.innerHeight;
    const centerX = width / 2;

    // <<< START: REVISED RESPONSIVE LOGIC >>>
    const isMobile = width < 768;

    const cardWidth = isMobile ? 320 : 615; // Using a fixed, narrower width for mobile
    const cardHeight = isMobile ? 80 : 60;  // Taller on mobile to fit two lines of text
    const font = isMobile ? '500 28px "Inter", sans-serif' : '500 34px "Inter", sans-serif';
    const lineHeight = isMobile ? 32 : 40; // Line height for wrapped text
    // <<< END: REVISED RESPONSIVE LOGIC >>>

    const text = "For 10 years, I've been wondering...";
    const textColor = "#1A1A1A";
    const boxColor = "rgba(255, 255, 255, 0.58)";
    const cornerRadius = 4;
    const fadeEndScroll = height * 0.55;
    const moveScale = 0.25;

    const opacity = Math.max(0, 1 - currentScrollY / fadeEndScroll);
    const targetCenterY = height / 2 - 100 + currentScrollY * moveScale;
    const cardX = (width - cardWidth) / 2;
    const cardY = targetCenterY - cardHeight / 2;

    if (opacity <= 0.01) {
        return null;
    }

    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.fillStyle = boxColor;
    ctx.beginPath();
    ctx.moveTo(cardX + cornerRadius, cardY);
    ctx.lineTo(cardX + cardWidth - cornerRadius, cardY);
    ctx.quadraticCurveTo(cardX + cardWidth, cardY, cardX + cardWidth, cardY + cornerRadius);
    ctx.lineTo(cardX + cardWidth, cardY + cardHeight - cornerRadius);
    ctx.quadraticCurveTo(cardX + cardWidth, cardY + cardHeight, cardX + cardWidth - cornerRadius, cardY + cardHeight);
    ctx.lineTo(cardX + cornerRadius, cardY + cardHeight);
    ctx.quadraticCurveTo(cardX, cardY + cardHeight, cardX, cardY + cardHeight - cornerRadius);
    ctx.lineTo(cardX, cardY + cornerRadius);
    ctx.quadraticCurveTo(cardX, cardY, cardX + cornerRadius, cardY);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = textColor;
    ctx.font = font;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // <<< CHANGED: Using the wrapText helper instead of fillText >>>
    const textPadding = 20;
    wrapText(ctx, text, centerX, targetCenterY + 2, cardWidth - textPadding, lineHeight);

    ctx.restore();

    return targetCenterY;
};

    const drawTransitionCard = (
    ctx: CanvasRenderingContext2D,
    currentScrollY: number,
    currentActiveSection: number
) => {
    // Skip transition card on mobile - cloud exit is handled separately
    if (window.innerWidth < 768) {
        return null;
    }
    
    if (currentActiveSection !== 11 && currentActiveSection !== 12) {
        return null;
    }
    
        // --- NEW: Use props directly from App.tsx. No more guessing! ---
        const section11StartPx = (neuronalSectionStartVh / 100) * window.innerHeight;
        const section11HeightPx = (neuronalSectionHeightVh / 100) * window.innerHeight;
    
        // This logic remains the same, but is now driven by reliable data.
        const scrollIntoSection11 = Math.max(0, currentScrollY - section11StartPx);
        const sectionProgress = section11HeightPx > 0 
            ? Math.min(1, scrollIntoSection11 / section11HeightPx) 
            : 0;

            // This is your new "knob" for tuning the overlap.
    // 0.8 means the handoff begins when the animation is 80% complete.
    const triggerPoint = window.innerWidth < 768 ? 0.5 : 0.8; // Earlier on mobile
if (sectionProgress >= triggerPoint && scrollDirection === 'down' && onNeuronalAnimationComplete && !animCompleteCalledRef.current) {
        onNeuronalAnimationComplete();
        animCompleteCalledRef.current = true; // Prevents this from being called more than once
    }    
        // --- Card Animation Timing (use more of the available space) ---
        const cardAppearStart = 0.03;   // Card starts appearing at 10% through section
        const cardAnimationEnd = 0.9;  // Card finishes at 90% through section
    
        if (sectionProgress < cardAppearStart) {
            return { isActive: false, isComplete: false, progress: sectionProgress };
        }
    
        // Calculate animation progress within the card's window
        const animDuration = cardAnimationEnd - cardAppearStart;
        const animProgress = (sectionProgress - cardAppearStart) / animDuration;
        const clampedAnimProgress = Math.max(0, Math.min(1, animProgress));
    
        // --- Card Rendering ---
const width = window.innerWidth;
const height = window.innerHeight;
const centerX = width / 2;

// Responsive card sizing (same pattern as title card)
const isMobile = width < 768;
const cardWidth = isMobile ? 320 : 545;
const cardHeight = isMobile ? 80 : 60;
const font = isMobile ? '500 28px "Inter", sans-serif' : '500 34px "Inter", sans-serif';
const lineHeight = isMobile ? 32 : 40;

const text = "And then looking for answers...";
const textColor = "#1A1A1A";
const boxColor = "rgba(255, 255, 255, 0.58)";
const cornerRadius = 4;
    
        // Smooth movement from top to bottom
        const startY = -cardHeight - 50;
        const endY = height + cardHeight + 50;
        const easedProgress = d3.easeCubicInOut(clampedAnimProgress);
        const currentY = startY + (endY - startY) * easedProgress;
    
        // Fade in/out at edges
        let opacity = 1;
        if (clampedAnimProgress < 0.15) {
            opacity = clampedAnimProgress / 0.15;
        } else if (clampedAnimProgress > 0.85) {
            opacity = (1 - clampedAnimProgress) / 0.15;
        }
    
        if (opacity <= 0.01) {
            return { isActive: false, isComplete: clampedAnimProgress >= 1, progress: sectionProgress };
        }
    
        // Draw the card
        const cardX = (width - cardWidth) / 2;
        const cardY = currentY - cardHeight / 2;
        
        ctx.save();
        ctx.globalAlpha = opacity;
        
        // Background box
        ctx.fillStyle = boxColor;
        ctx.beginPath();
        ctx.moveTo(cardX + cornerRadius, cardY);
        ctx.lineTo(cardX + cardWidth - cornerRadius, cardY);
        ctx.quadraticCurveTo(cardX + cardWidth, cardY, cardX + cardWidth, cardY + cornerRadius);
        ctx.lineTo(cardX + cardWidth, cardY + cardHeight - cornerRadius);
        ctx.quadraticCurveTo(cardX + cardWidth, cardY + cardHeight, cardX + cardWidth - cornerRadius, cardY + cardHeight);
        ctx.lineTo(cardX + cornerRadius, cardY + cardHeight);
        ctx.quadraticCurveTo(cardX, cardY + cardHeight, cardX, cardY + cardHeight - cornerRadius);
        ctx.lineTo(cardX, cardY + cornerRadius);
        ctx.quadraticCurveTo(cardX, cardY, cardX + cornerRadius, cardY);
        ctx.closePath();
        ctx.fill();
        
        // Text
        ctx.fillStyle = textColor;
        ctx.font = font;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const textPadding = 20;
wrapText(ctx, text, centerX, currentY + 2, cardWidth - textPadding, lineHeight);
        
        ctx.restore();
    
        return { 
            isActive: true, 
            isComplete: clampedAnimProgress >= 1, 
            progress: sectionProgress,
            animProgress: clampedAnimProgress 
        };
    };

    // In D3Cloud.tsx
    // Replace the existing startAnimation function with this:

    const startAnimation = () => {
        if (!canvasRef.current) {
            console.error("!!! Canvas ref not found!");
            return;
        }
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) { 
            console.error("!!! Failed to get 2D context!");
            return; 
        }
    
        updateCanvasSize();
        window.addEventListener("resize", updateCanvasSize);
    
        let lastFrameTime = performance.now();
    
        const animate = (time: number) => {
            // --- Setup ---
            const deltaTime = Math.min(time - lastFrameTime) / 1000;
            lastFrameTime = time;
            const viewWidth = window.innerWidth;
            const viewHeight = window.innerHeight;
            const localCtx = canvasRef.current?.getContext("2d");
            if (!localCtx) return;

            // --- ADDED: Progress Calculation ---
        const section11StartPx = (neuronalSectionStartVh / 100) * window.innerHeight;
        const section11TimelinePx = (neuronalSectionHeightVh / 100) * window.innerHeight;
        const scrollIntoSection11 = Math.max(0, scrollYRef.current - section11StartPx);
        const progress = section11TimelinePx > 0 ? Math.min(1, scrollIntoSection11 / section11TimelinePx) : 0;
    
            // --- 1. Update Core Systems ---
            if (simulationRef.current && simulationRef.current.alpha() > simulationRef.current.alphaMin()) {
                simulationRef.current.tick();
            }
            
            particlesRef.current.forEach((particle) => {
                try {
                    updateParticle(particle, deltaTime);
                } catch (error) {
                    console.error(`Error updating particle ${particle.id}:`, error);
                }
            });
    
            if (activeSectionRef.current === 11) {
                updateConnections(deltaTime);
            }
    
            // --- 2. Section 11 Animation Orchestration ---
const isExiting = cloudExitStartedRef.current;

// Determine if we should START the exit animation
if (activeSectionRef.current === 11) {
    const cardResult = drawTransitionCard(localCtx, scrollYRef.current, activeSectionRef.current);
    const cloudExitTrigger = 0.6; // The point in the card's travel where the cloud starts leaving

    if (cardResult && cardResult.animProgress !== undefined && cardResult.animProgress >= cloudExitTrigger && !isExiting) {
        // Start the exit!
        cloudExitStartedRef.current = true;
    }
}

// If the user scrolls back up before the exit is complete, reverse it
if (scrollDirection === 'up' && isExiting) {
    cloudExitStartedRef.current = false;
}

// Now, handle the animation progress smoothly using deltaTime
const exitAnimationDuration = 1.0; // 1 second to fly off screen
if (cloudExitStartedRef.current && cloudExitProgressRef.current < 1) {
    cloudExitProgressRef.current = Math.min(1, cloudExitProgressRef.current + (deltaTime / exitAnimationDuration));
} else if (!cloudExitStartedRef.current && cloudExitProgressRef.current > 0) {
    cloudExitProgressRef.current = Math.max(0, cloudExitProgressRef.current - (deltaTime / exitAnimationDuration));
}

// Calculate the offset based on our STABLE progress ref
const easedExitProgress = d3.easeCubicIn(cloudExitProgressRef.current); // Use easeCubicIn for a nice acceleration
cloudExitOffsetRef.current = -(viewHeight + 300) * easedExitProgress;

// The handoff to the next section (the feature scroll) happens when the cloud is mostly gone
if (cloudExitProgressRef.current > 0.8 && onCloudExitComplete && !handoffCompleteRef.current) {
    onCloudExitComplete();
    handoffCompleteRef.current = true;
}

// Reset handoff flag when leaving these sections, allowing it to happen again
if (activeSectionRef.current !== 11 && activeSectionRef.current !== 12) {
    handoffCompleteRef.current = false;
}
    
            // --- 3. Drawing (Unified Logic) ---
        localCtx.clearRect(0, 0, viewWidth, viewHeight);

        // This single, unified system will now handle all particle drawing.
        // It respects the progress kill switch and ensures consistent layering at all times.
        if (progress < 1) {
            const currentActiveCategory = activeCategoryRef.current;

            // We apply the exit offset globally. It will be 0 except during the exit animation.
            localCtx.save();
            localCtx.translate(0, cloudExitOffsetRef.current);

            // PASS 1: Draw all background (non-question) particles, floating or frozen.
            particlesRef.current.forEach(particle => {
                if (particle.question === null) {
                    drawParticle(localCtx, particle);
                }
            });

            // PASS 2: Draw all QUESTION particles that are NOT in the active category.
            particlesRef.current.forEach(particle => {
                if (particle.question !== null && particle.category !== currentActiveCategory) {
                    drawParticle(localCtx, particle);
                }
            });

            // PASS 3: Draw the ACTIVE question particles last, so they are always on top.
            particlesRef.current.forEach(particle => {
                if (particle.question !== null && particle.category === currentActiveCategory) {
                    drawParticle(localCtx, particle);
                }
            });

            // Also draw the connections inside the translated space
            if (connectionsRef.current.length > 0) {
                try {
                    drawConnections(localCtx);
                } catch (error) {
                    console.error("Error drawing connections:", error);
                }
            }

            localCtx.restore();
        }
    
            // --- 4. Draw UI Elements ---
if (activeSectionRef.current === 0) {
    try {
        drawTitleCard(localCtx, scrollYRef.current, activeSectionRef.current);
    } catch (error) {
        console.error("Error drawing title card:", error);
    }
}

if (activeSectionRef.current === 11) {
    try {
        drawTransitionCard(localCtx, scrollYRef.current, activeSectionRef.current);
    } catch (error) {
        console.error("Error drawing transition card:", error);
    }
}
    
            // --- 5. Draw Section Headers (for grid sections) ---
const currentActiveSection = activeSectionRef.current;
if (currentActiveSection > 0 && currentActiveSection <= questionData.length) {
    const gridBoundsData = activeGridBoundsRef.current; // Read the full data object
    const category = questionData[currentActiveSection - 1];
    const lastChangeTime = lastHeaderChangeTimeRef.current;
    const FADE_DURATION_MS = 1700;
    const TARGET_OPACITY = 0.8;

    // VVV NEW SAFETY CHECK VVV
    // Only proceed if we have bounds AND those bounds match the current section's category
    if (gridBoundsData && category && gridBoundsData.categoryId === category.id && lastChangeTime && lastChangeTime > 0) {
        // ... (fade logic is the same)
        const currentTime = performance.now();
        const elapsedTime = currentTime - lastChangeTime;
        const progress = Math.min(1, elapsedTime / FADE_DURATION_MS);
        const easedProgress = d3.easeCubicInOut(progress);
        const calculatedHeaderOpacity = easedProgress * TARGET_OPACITY;

        if (calculatedHeaderOpacity > 0.01) {
            const categoryTitle = category.header;
            if (categoryTitle) {
                try {
                    // Pass the correct nested bounds object
                    drawSectionHeader(localCtx, categoryTitle, gridBoundsData.bounds, calculatedHeaderOpacity);
                } catch (error) {
                    console.error("Error drawing section header:", error);
                }
            }
        }
    }
}
    
            // --- 6. Request Next Frame ---
            animationFrameRef.current = requestAnimationFrame(animate);
        };
    
        // Start the animation loop
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = requestAnimationFrame(animate);
    };

    // In D3Cloud.tsx
// Replace your existing updateParticle function with this ENTIRE block:

const updateParticle = (particle: Particle, deltaTime: number) => {
    const currentParticleBaseWidth = PARTICLE_BASE_WIDTH * PARTICLE_SIZE_MULTIPLIER;
    const currentParticleBaseHeight = PARTICLE_BASE_HEIGHT * PARTICLE_SIZE_MULTIPLIER;
    const MOVE_DURATION = 1.5;
const RETURN_DURATION = 1.0;
const MOVE_POSITION_EASING = d3.easeCubicInOut;
const RETURN_EASING = d3.easeCubicInOut;
const CIRCLE_EASING = d3.easeCubicInOut;

    if (isNaN(deltaTime) || deltaTime <= 0) {
        deltaTime = 1 / 60;
    }

    if (particle.hasAppeared !== true) {
        const currentTime = performance.now();
        const readyToAppear = (particle.appearDelay !== undefined && currentTime >= particle.appearDelay);

        if (readyToAppear) {
            // --- NEW "IGNITION" LOGIC ---
            // This block runs only ONCE per particle, the first moment it's ready to appear.
            if (!particle.hasBeenLaunched) {
                // Give the particle its outward velocity "kick"
                const bloomAngle = Math.random() * Math.PI * 2;
                const bloomSpeed = 0.04 + Math.random() * 1.0; // 0.1 and 1.1 is pretty good but that was before lessening friction
                particle.vx = Math.cos(bloomAngle) * bloomSpeed;
                particle.vy = Math.sin(bloomAngle) * bloomSpeed;
                
                // Mark it as launched so this never runs again for this particle.
                particle.hasBeenLaunched = true;
            }

            // This appearance animation logic now runs on a particle that has velocity.
            const appearDuration = 1.0 + Math.random() * 1.5; 
            particle.introProgress = (particle.introProgress ?? 0) + deltaTime / appearDuration;
            particle.introProgress = Math.min(1, particle.introProgress);
            
            const easedProgress = d3.easeCubicOut(particle.introProgress ?? 0);
            particle.opacity = easedProgress;

            const targetW = particle.targetWidth || currentParticleBaseWidth;
            const targetH = particle.targetHeight || currentParticleBaseHeight;
            particle.width = targetW;
            particle.height = targetH;
    
            if (particle.introProgress >= 1) {
                particle.hasAppeared = true;
                particle.opacity = 1;
                particle.introProgress = 1;
            }
        } else {
            // While waiting for its delay, do nothing. It sits at the center, invisible.
            particle.opacity = 0;
            particle.width = 0;
            particle.height = 0;
        }
    }

    if (particle.state !== "frozen") {
        if (particle.fx !== null) particle.fx = null;
        if (particle.fy !== null) particle.fy = null;
    }
    
        // The rest of the switch statement remains the same as your original code...
        switch (particle.state) {
          case "floating":
            if (particle.hasAppeared) {
                particle.width = particle.targetWidth ?? currentParticleBaseWidth;
                particle.height = particle.targetHeight ?? currentParticleBaseHeight;
                particle.opacity = 1;
            }
            particle.progress = 0;
            particle.textOpacity = 0;
            // ... (rest of floating case is the same)
            particle.widthAtArrangeStart = undefined; particle.heightAtArrangeStart = undefined;
            particle.startX = undefined; particle.startY = undefined;
            particle.targetX = null; particle.targetY = null;
            particle.extendedWidth = undefined; particle.extendedHeight = undefined;
            particle.frozenX = undefined; particle.frozenY = undefined;
            break;
    
          case "frozen":
            particle.vx = 0;
            particle.vy = 0;
            if (particle.frozenX !== undefined) particle.x = particle.frozenX;
            if (particle.frozenY !== undefined) particle.y = particle.frozenY;
            
            particle.width = particle.targetWidth ?? currentParticleBaseWidth;
            particle.height = particle.targetHeight ?? currentParticleBaseHeight;
            particle.opacity = 1;
            particle.textOpacity = 0;
            particle.progress = 1;
            break;
        
          // ... NO CHANGES TO ANY OF THE OTHER CASES BELOW THIS LINE ...
          // (movingToGrid, arranging, in-section, returning, etc. are all identical to your original file)
    
          case "movingToGrid": {
            const hasGridTarget = particle.targetX != null && particle.targetY != null;
            if (!hasGridTarget || particle.startX == null || particle.startY == null || particle.extendedWidth == null || particle.extendedHeight == null || particle.rowIndex == null || particle.colIndex == null ) {
                particle.state = "floating"; particle.progress = 0; particle.width = currentParticleBaseWidth; particle.height = currentParticleBaseHeight; break;
            }
            const moveDuration = MOVE_DURATION; const staggerFactor = (particle.rowIndex + particle.colIndex) * 0.05; const maxStagger = 0.3; const cappedStagger = Math.min(staggerFactor, maxStagger);
            const totalMoveTime = moveDuration + cappedStagger;
particle.progress = Math.min(totalMoveTime, particle.progress + deltaTime); // Note: We now track time, not a 0-1 progress
        // Calculate the individual progress of the staggered journey (from 0 to 1)
const timeIntoAnimation = Math.max(0, particle.progress - cappedStagger);
const individualProgress = Math.min(1, timeIntoAnimation / moveDuration);
const moveEasedProgress = MOVE_POSITION_EASING(individualProgress);
            particle.x = particle.startX + ((particle.targetX ?? particle.startX) - particle.startX) * moveEasedProgress;
            particle.y = particle.startY + ((particle.targetY ?? particle.startY) - particle.startY) * moveEasedProgress;
            particle.vx = 0; particle.vy = 0; const ELONG_THRESH = 0.8;
            if (moveEasedProgress < ELONG_THRESH) {
                const scaleProg = moveEasedProgress / ELONG_THRESH; const scaleFactor = 1.0 + (1.4 - 1.0) * d3.easeCubicInOut(scaleProg); // Re-added TARGET_SCALE_FACTOR for clarity
                particle.width = currentParticleBaseWidth * scaleFactor; particle.height = currentParticleBaseHeight * scaleFactor; particle.textOpacity = 0;
            } else {
                const elongProg = (moveEasedProgress - ELONG_THRESH) / (1 - ELONG_THRESH); const threshSF = 1.4;
                const threshW = currentParticleBaseWidth * threshSF; const threshH = currentParticleBaseHeight * threshSF; 
                const hProg = d3.easeCubicOut(elongProg); const wProg = d3.easeCubicInOut(elongProg);
                particle.height = threshH + (particle.extendedHeight - threshH) * hProg; particle.width = threshW + (particle.extendedWidth - threshW) * wProg;
                const timeInText = elongProg - 0.2; let textOpProg = 0; // Re-added TEXT_FADE_START
                if (timeInText > 0 && 0.5 > 1e-6) { textOpProg = Math.min(1, Math.max(0, timeInText / 0.5)); // Re-added TEXT_FADE_DURATION
                } else if (timeInText >= 0.5) { textOpProg = 1; }
                particle.textOpacity = d3.easeCubicOut(textOpProg); if (elongProg <= 0.2) { particle.textOpacity = 0; }
            }
            if (individualProgress >= 1) {
                particle.x = particle.targetX ?? particle.x; particle.y = particle.targetY ?? particle.y;
                particle.width = particle.extendedWidth; particle.height = particle.extendedHeight;
                particle.textOpacity = 1; particle.state = "in-section";
                particle.startX = undefined; particle.startY = undefined;
            }
            break;
          }
          case "arranging": {
            const hasGridTarget = particle.targetX != null && particle.targetY != null;
            if (!hasGridTarget || particle.extendedWidth == null || particle.extendedHeight == null || particle.widthAtArrangeStart == null || particle.heightAtArrangeStart == null || particle.rowIndex == null || particle.colIndex == null ) {
                particle.state = "floating"; particle.progress = 0; particle.width = currentParticleBaseWidth; particle.height = currentParticleBaseHeight; break;
            }
            particle.x = particle.targetX ?? particle.x; particle.y = particle.targetY ?? particle.y;
            particle.progress = Math.min(1, particle.progress + deltaTime / 1.0); const staggerDelaySec = (particle.rowIndex + particle.colIndex) * 0.1;
            const currentTimeInArrange = particle.progress * 1.0; const effectiveArrangeDuration = 1.0 - staggerDelaySec; let arrangeProgressStaggered = 0;
            if ( currentTimeInArrange > staggerDelaySec && effectiveArrangeDuration > 1e-6 ) { const timeSinceStagger = currentTimeInArrange - staggerDelaySec; arrangeProgressStaggered = Math.min( 1, Math.max(0, timeSinceStagger / effectiveArrangeDuration) );
            } else if (currentTimeInArrange <= staggerDelaySec) { arrangeProgressStaggered = 0; } else { arrangeProgressStaggered = 1; }
            const heightEased = d3.easeCubicOut(arrangeProgressStaggered); const widthEased = d3.easeCubicInOut(arrangeProgressStaggered);
            particle.width = particle.widthAtArrangeStart + (particle.extendedWidth - particle.widthAtArrangeStart) * widthEased;
            particle.height = particle.heightAtArrangeStart + (particle.extendedHeight - particle.heightAtArrangeStart) * heightEased;
            const textProgress = Math.max(0, (widthEased - 0.2) / 0.5 ); particle.textOpacity = Math.min(1, textProgress);
            particle.vx = 0; particle.vy = 0;
            if (particle.progress >= 1) {
                particle.width = particle.extendedWidth; particle.height = particle.extendedHeight;
                particle.textOpacity = 1; particle.state = "in-section"; particle.progress = 1; 
                particle.widthAtArrangeStart = undefined; particle.heightAtArrangeStart = undefined;
            }
            break;
          }
          case "in-section":
            if (particle.targetX != null) particle.x = particle.targetX;
            if (particle.targetY != null) particle.y = particle.targetY;
            if (particle.extendedWidth != null) particle.width = particle.extendedWidth;
            if (particle.extendedHeight != null) particle.height = particle.extendedHeight;
            particle.textOpacity = 1; particle.vx = 0; particle.vy = 0;
            particle.opacity = 1;
            break;
    
          case "collapsing": {
            const collapseDuration = 0.5; particle.progress = Math.min(1, particle.progress + deltaTime / collapseDuration );
            const collapseEased = RETURN_EASING(particle.progress); 
            if (particle.startX === undefined) particle.startX = particle.x; 
            if (particle.startY === undefined) particle.startY = particle.y; 
            if (particle.widthAtArrangeStart === undefined && particle.extendedWidth !== undefined) particle.widthAtArrangeStart = particle.extendedWidth; 
            if (particle.heightAtArrangeStart === undefined && particle.extendedHeight !== undefined) particle.heightAtArrangeStart = particle.extendedHeight; 
            const startW = particle.widthAtArrangeStart || currentParticleBaseWidth; const startH = particle.heightAtArrangeStart || currentParticleBaseHeight;
            if (particle.targetX != null) particle.x = particle.targetX; 
            if (particle.targetY != null) particle.y = particle.targetY;
            particle.textOpacity = 1.0 - collapseEased; 
            particle.width = startW + (currentParticleBaseWidth - startW) * collapseEased;
            particle.height = startH + (currentParticleBaseHeight - startH) * collapseEased;
            particle.opacity = 1.0 - (collapseEased * 0.5);
            particle.vx = 0; particle.vy = 0;
            if (particle.progress >= 1) {
                particle.state = "returning"; particle.progress = 0;
                particle.width = currentParticleBaseWidth; particle.height = currentParticleBaseHeight;
                particle.targetX = null; particle.targetY = null;
                particle.extendedWidth = undefined; particle.extendedHeight = undefined;
                particle.widthAtArrangeStart = undefined; particle.heightAtArrangeStart = undefined;
                particle.startX = particle.x; particle.startY = particle.y;
            }
            break;
          }
          case "returning": {
            particle.progress = Math.min(1, particle.progress + deltaTime / RETURN_DURATION );
            const returnEased = RETURN_EASING(particle.progress);
            if (particle.startX === undefined) particle.startX = particle.x; if (particle.startY === undefined) particle.startY = particle.y;
            const endX = particle.targetCenterX ?? window.innerWidth / 2; const endY = particle.targetCenterY ?? window.innerHeight / 2;
            particle.x = particle.startX + (endX - particle.startX) * returnEased;
            particle.y = particle.startY + (endY - particle.startY) * returnEased;
            if(particle.width !== currentParticleBaseWidth || particle.height !== currentParticleBaseHeight){
                const startWidthRet = particle.widthAtArrangeStart || particle.extendedWidth || particle.width; 
                const startHeightRet = particle.heightAtArrangeStart || particle.extendedHeight || particle.height; 
                particle.width = startWidthRet + (currentParticleBaseWidth - startWidthRet) * returnEased;
                particle.height = startHeightRet + (currentParticleBaseHeight - startHeightRet) * returnEased;
            } else {
                particle.width = currentParticleBaseWidth;
                particle.height = currentParticleBaseHeight;
            }
            particle.textOpacity = 0;
            particle.opacity = 1;
            particle.vx = 0; particle.vy = 0;
            if (particle.progress >= 1) {
                particle.state = "floating"; particle.progress = 0;
                particle.width = currentParticleBaseWidth; particle.height = currentParticleBaseHeight;
                particle.startX = undefined; particle.startY = undefined;
                particle.targetX = null; particle.targetY = null;
                particle.extendedWidth = undefined; particle.extendedHeight = undefined;
                particle.widthAtArrangeStart = undefined; particle.heightAtArrangeStart = undefined;
                particle.frozenX = undefined; particle.frozenY = undefined; 
                if (simulationRef.current && simulationRef.current.alpha() < 0.1) { simulationRef.current.alpha(0.1).restart(); }
            }
            break;
          }

          default:
            particle.state = "floating";
            particle.progress = 0;
            particle.fx = null; 
            particle.fy = null;
            particle.width = currentParticleBaseWidth;
            particle.height = currentParticleBaseHeight;
            particle.opacity = 1;
            particle.frozenX = undefined; 
            particle.frozenY = undefined;
            break;
        }
      };

    // Replace your existing drawSectionHeader function with this:
    const drawSectionHeader = (
        ctx: CanvasRenderingContext2D,
        categoryTitle: string,
        gridBounds: { minX: number; maxX: number; minY: number },
        opacity: number // It now uses this parameter!
    ) => {
        // Basic checks
        if (
            !categoryTitle ||
            !isFinite(gridBounds.minX) ||
            !isFinite(gridBounds.minY) ||
            opacity <= 0.01 // Check passed opacity here
        ) {
            return;
        }

        // Styling constants
        const headerFont = '500 24px "Inter", sans-serif';
        const fontSize = 24;
        const textColor = "rgba(0, 0, 0, 0.8)";
        const boxFillColor = "rgba(255, 255, 255, 0.85)";
        const boxCornerRadius = 4;
        const horizontalPadding = 16;
        const verticalPadding = 8;
        const spaceAboveGrid = 15;

        // Calculations
        ctx.font = headerFont;
        const textMetrics = ctx.measureText(categoryTitle);
        const textWidth = textMetrics.width;
        const boxWidth = textWidth + 2 * horizontalPadding;
        const boxHeight = fontSize + 2 * verticalPadding;
        const boxX = gridBounds.minX;
        const boxY = gridBounds.minY - spaceAboveGrid - boxHeight;
        const textX = boxX + horizontalPadding;
        const textY = boxY + boxHeight / 2;

        // Apply the opacity passed as a parameter
        ctx.globalAlpha = opacity; // Uses the function parameter

        // Draw background box
        ctx.fillStyle = boxFillColor;
        ctx.beginPath();
        ctx.moveTo(boxX + boxCornerRadius, boxY);
        ctx.lineTo(boxX + boxWidth - boxCornerRadius, boxY);
        ctx.quadraticCurveTo(
            boxX + boxWidth,
            boxY,
            boxX + boxWidth,
            boxY + boxCornerRadius
        );
        ctx.lineTo(boxX + boxWidth, boxY + boxHeight - boxCornerRadius);
        ctx.quadraticCurveTo(
            boxX + boxWidth,
            boxY + boxHeight,
            boxX + boxWidth - boxCornerRadius,
            boxY + boxHeight
        );
        ctx.lineTo(boxX + boxCornerRadius, boxY + boxHeight);
        ctx.quadraticCurveTo(
            boxX,
            boxY + boxHeight,
            boxX,
            boxY + boxHeight - boxCornerRadius
        );
        ctx.lineTo(boxX, boxY + boxCornerRadius);
        ctx.quadraticCurveTo(boxX, boxY, boxX + boxCornerRadius, boxY);
        ctx.closePath();
        ctx.fill();

        // Draw text
        ctx.fillStyle = textColor;
        ctx.font = headerFont;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        try {
            ctx.fillText(categoryTitle, textX, textY);
        } catch (e) {
            console.error("Error during drawSectionHeader fillText:", e);
        }

        // Reset globalAlpha
        ctx.globalAlpha = 1.0;
    };

    // In D3Cloud.tsx

    const drawParticle = (ctx: CanvasRenderingContext2D, particle: Particle) => {
        // --- Variable Setup (no changes here) ---
        const w = particle.width;
        const h = particle.height;
        const x = particle.x ?? window.innerWidth / 2;
        const y = particle.y ?? window.innerHeight / 2;
        const { color, question, state } = particle;
        
        if (isNaN(x) || isNaN(y) || isNaN(w) || isNaN(h) || w <= 0 || h <= 0) {
            return; 
        }
        
        let finalOpacity: number;
        const time = performance.now() * 0.001;
        const pulseSpeed = 0.5;
        const pulseAmount = 0.3;
        const baseForPulse = 0.8;
        const uniqueOffset = (parseInt(particle.id.replace(/\D/g, ""), 10) || 0) * 0.2;
        const pulseValue = baseForPulse + Math.sin(time * pulseSpeed + uniqueOffset) * pulseAmount;
    
        if (particle.hasAppeared !== true) {
            const easedProgress = d3.easeCubicOut(particle.introProgress ?? 0);
            finalOpacity = pulseValue * easedProgress;
        } else {
            if (ANIMATION_OPTIONS.enableOpacityPulse && (particle.state === "floating" || particle.state === "frozen")) {
                finalOpacity = pulseValue;
            } else {
                finalOpacity = 1.0;
            }
        }
        
        finalOpacity = Math.max(0, Math.min(1, finalOpacity));
        
        if (finalOpacity < 0.01) {
            return;
        }
        
        // --- Drawing Operations (no changes up to here) ---
        ctx.globalAlpha = finalOpacity;
        ctx.shadowColor = "transparent";
        ctx.shadowBlur = 0;
        
        if (ANIMATION_OPTIONS.enableTrails && particle.trail && particle.trail.length > 0) {
            // ... trail drawing logic is unchanged ...
        }
        
        ctx.fillStyle = color;
        ctx.beginPath();
        const cornerRadius = Math.min(w / 2, h / 2, 4);
        ctx.moveTo(x + cornerRadius, y);
        ctx.lineTo(x + w - cornerRadius, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + cornerRadius);
        ctx.lineTo(x + w, y + h - cornerRadius);
        ctx.quadraticCurveTo(x + w, y + h, x + w - cornerRadius, y + h);
        ctx.lineTo(x + cornerRadius, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - cornerRadius);
        ctx.lineTo(x, y + cornerRadius);
        ctx.quadraticCurveTo(x, y, x + cornerRadius, y);
        ctx.closePath();
        ctx.fill();
        
        if (ANIMATION_OPTIONS.enableShimmer && particle.state === "floating" && particle.hasAppeared) {
            // ... shimmer logic is unchanged ...
        }
        
        // --- Draw Text (This section is now responsive) ---
        if (
            (state === "in-section" || state === "arranging" || state === "movingToGrid") &&
            question &&
            particle.textOpacity != null &&
            particle.textOpacity > 0 &&
            particle.hasAppeared
        ) {
            ctx.save();
            ctx.beginPath();
            ctx.rect(x, y, w, h); 
            ctx.clip();
            
            ctx.fillStyle = "rgba(0, 0, 0, 0.8)";
            
            // <<< START: RESPONSIVE FONT LOGIC >>>
            const isMobile = window.innerWidth < 768;
            const scaleFactor = isMobile ? 0.9 : 0.95;
            const fontSize = isMobile ? 18 : 20;
            const padding = isMobile ? 14 : 16;
            
            ctx.font = `300 ${fontSize * scaleFactor}px "Inter", sans-serif`;
            // <<< END: RESPONSIVE FONT LOGIC >>>
    
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            
            ctx.globalAlpha = finalOpacity * particle.textOpacity; 
            
            const textX = x + (padding * scaleFactor); // Use responsive padding
            const textY = y + h / 2 + 1;
            const maxTextWidth = w - (2 * padding * scaleFactor); // Use responsive padding
            
            if (maxTextWidth > 0) {
                ctx.fillText(question, textX, textY, maxTextWidth);
            }
            ctx.restore();
        }
        
        ctx.globalAlpha = 1.0; 
    };


    const canvasZIndex = activeSectionRef.current === 11 ? 10 : 1;

return (
    <canvas
        ref={canvasRef}
        className="particle-cloud"
        style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: canvasZIndex, // <<< NOW DYNAMIC
            // background: 'rgba(255,0,0,0.1)' // Temp background for debugging visibility
        }}
    />
);
}; // --- End of D3Cloud Component ---

export default memo(D3Cloud);